import _ from 'underscore'
import Backbone from '@polleverywhere/backbone'
import store from '@polleverywhere/redux'
import { slice as appSlice } from '../../slices/app'
import { fragmentToPathData, pathDataToUrl, pathDataFromState } from './util/historyPathData'
import { setGroupKey } from '../../participant-components/register/util/groupKey'
import { peUrlParams } from '../../../entry/util/peUrlParams'
import { selectActivePresenter } from '../../states/participant/redux/activePresenter'
import { selectParticipantRegistrations } from '../../states/participant/redux/participantRegistrations'
import { selectRegisteredParticipantsOnly } from '../../states/participant/redux/selectors/registrationStatus'

function getResponseHistoryRoute (pathData) {
  if (!pathData) return

  const urlPathData = fragmentToPathData(Backbone.history.fragment)

  if (!_.isEqual(urlPathData, pathData)) {
    return pathDataToUrl(pathData)
  }
}

function updateRoute () {
  const page = store.getState()[appSlice.name].page
  const activePresenter = selectActivePresenter(store.getState())
  const pageToRoute = _.invert(this.routes)
  const navOptions = {
    trigger: false,
    replace: false
  }
  let route
  let title

  if (_.has(pageToRoute, page)) {
    route = pageToRoute[page]
    title = route.replace(/[_/-]/g, ' ').replace(/^\w/, c => c.toUpperCase())
  } else if (page === 'live_response' && activePresenter.login) {
    route = activePresenter.login
    title = `${route}'s presentation`
  } else if (page === 'response_history') {
    const pathData = pathDataFromState()
    route = getResponseHistoryRoute.call(this, pathData, navOptions)
    title = `Response history${pathData?.detail ? ' detail' : ''}`
  } else if (page === 'register' && activePresenter.login) {
    route = `${activePresenter.login}/register`
    title = `Register with ${activePresenter.login}`

    const fromResponsePage = ['live_response', 'static_response'].includes(store.getState()[appSlice.name].previousPage)
    const wasRegistrationRequired = selectRegisteredParticipantsOnly(store.getState())
    if (fromResponsePage && wasRegistrationRequired) {
      navOptions.replace = true
    }
  } else if (page === 'registrations') {
    const regState = selectParticipantRegistrations(store.getState())
    if (regState?.focussedRegistration) {
      route = `registrations/${regState.focussedRegistration}`
      title = 'Registration detail'
    } else {
      route = 'registrations'
      title = 'Registrations'
    }
  }

  if (route && route !== Backbone.history.fragment) {
    this.navigate(route, navOptions)
  }

  if (title) {
    document.title = `${title} | Poll Everywhere`
  }
}

// Ensure all .../web and .../intro routes are moved to .../respond
function replaceLegacyWithRespond (type, participantKey) {
  if (/\/web|\/intro/.test(Backbone.history.fragment)) {
    this.navigate(`${type}/${participantKey}/respond`, {
      trigger: false,
      replace: true
    })
  }
}

async function loadRegistrations (id) {
  store.dispatch(appSlice.pageUpdated('registrations'))
  if (id) {
    this.state.changePage('registrations', { id })
  }
}

function loadResponseHistory () {
  store.dispatch(appSlice.pageUpdated('response_history'))
  this.state.changePage('response_history', fragmentToPathData(Backbone.history.fragment))
}

function loadSettingsSection (section = 'show') {
  this.state.appStates.settings.set('page', section)
  this.state.changePage('settings')
}

/**
 * Updates URL when state changes. Also, update the state model
 * from the initial route.
 */
export default class Router extends Backbone.Router {
  get routes () {
    return {
      // we have a wildcard route in :presenter so we need to override all the core routes here otherwise everything will go into the wildcard handler
      home: 'home',
      support: 'support',
      feedback: 'feedback',
      profile: 'settings',
      signup: 'signup',
      reset_password: 'reset_password',
      settings: 'settings',
      'settings(/:section)': loadSettingsSection.bind(this),
      'response_history(/:id)(?*queryString)': loadResponseHistory.bind(this),
      'registrations(/:id)': loadRegistrations.bind(this),
      keyword: 'legacy_vote',
      vote: 'legacy_vote',
      register: 'register_search',
      present: 'present',
      login: 'login',
      logout: 'logout',

      // *args here handles all the weird crap that embedders like facebook append to the URL
      ':type/:participant_key/(respond|web|intro)*args': (type, key) => {
        replaceLegacyWithRespond.call(this, type, key)
        this.state.showIndividualActivity(type, key)
      },

      ':presenter/register(?*queryString)': (presenter) => {
        const params = peUrlParams()
        const groupKey = params.get('group_key') || ''
        if (groupKey) {
          // Store this in sessionStorage so we don't lose it if they go through SSO or something
          setGroupKey({ presenter, groupKey })
        }
        this.state.enterRegisterFlow(presenter)
      },
      // Legacy routes redirect
      ':presenter/(redeem|get_code|purchase|reminder)': (presenter) => {
        this.navigate(`${presenter}/register`, { replace: true })
      },
      // Live session page
      ':presenter': (presenter) => {
        this.state.joinPresenterSession(presenter)
      }
    }
  }

  initialize (options = {}) {
    _.defaults(options, {
      updateURL: true
    })

    this.state = options.state

    // Update route based on state changes
    if (options.updateURL) {
      store.subscribe(() => {
        updateRoute.call(this)
      })

      this.listenTo(this.state.appStates.registrations, 'change', () => updateRoute.call(this))

      // If we hit a named route, update the url. e.g. settings, login, etc.
      this.on('route', (route, params) => {
        if (route.length) {
          store.dispatch(appSlice.pageUpdated(route))
        }
      })
    }
  }
}
